// Finance design tokens — built on the app's Foxbeep theme (C) so finance
// screens stay on-brand while using a calmer, more consistent SaaS look.
import { C } from "../../ui/core";

export const T = {
    // surfaces
    bg: C.bg,
    surface: C.card,
    surface2: C.card2,
    field: "#0d1526",
    border: C.border,
    borderSoft: "#1a2740",
    // text
    text: C.text,
    textStrong: C.white,
    textMuted: "#94a3b8",   // readable secondary text on dark surfaces
    textFaint: C.muted,
    // brand + semantic
    primary: C.accent,
    primaryHover: "#2f6fdc",
    success: C.green,
    warning: C.yellow,
    danger: C.red,
    info: C.accent2,
    // shape
    radius: 12,
    radiusSm: 8,
    radiusPill: 999,
    shadow: "0 1px 2px rgba(0,0,0,.25)",
    shadowPop: "0 12px 32px rgba(0,0,0,.45)",
    // spacing scale
    s1: 4, s2: 8, s3: 12, s4: 16, s5: 20, s6: 24, s8: 32,
    // type
    font: "'DM Sans', 'Segoe UI', sans-serif",
};

export const text = {
    pageTitle: { fontSize: 22, fontWeight: 700, color: T.textStrong, margin: 0, letterSpacing: -0.2 },
    pageSub: { fontSize: 13, color: T.textMuted, margin: "4px 0 0" },
    section: { fontSize: 15, fontWeight: 600, color: T.textStrong, margin: 0 },
    label: { fontSize: 12, fontWeight: 600, color: T.textMuted, display: "block", marginBottom: 6 },
    caption: { fontSize: 12, color: T.textMuted },
    money: { fontVariantNumeric: "tabular-nums", fontWeight: 600, color: T.textStrong, whiteSpace: "nowrap" },
};

export const surface = {
    card: { background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.radius, boxShadow: T.shadow },
    inset: { background: T.surface2, border: `1px solid ${T.borderSoft}`, borderRadius: T.radiusSm },
};

export const control = {
    input: {
        background: T.field, border: `1px solid ${T.border}`, borderRadius: T.radiusSm,
        padding: "10px 12px", color: T.text, fontSize: 14, width: "100%", outline: "none",
        boxSizing: "border-box", fontFamily: T.font, minHeight: 40,
    },
};

export function button(variant = "primary", size = "md") {
    const pad = size === "sm" ? "6px 12px" : "9px 16px";
    const base = {
        display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
        borderRadius: T.radiusSm, padding: pad, fontSize: size === "sm" ? 13 : 14, fontWeight: 600,
        cursor: "pointer", border: "1px solid transparent", fontFamily: T.font, whiteSpace: "nowrap",
        minHeight: size === "sm" ? 32 : 40, transition: "background .15s, border-color .15s, opacity .15s",
    };
    if (variant === "primary") return { ...base, background: T.primary, color: "#fff" };
    if (variant === "success") return { ...base, background: T.success, color: "#fff" };
    if (variant === "danger") return { ...base, background: "transparent", color: T.danger, borderColor: T.danger + "55" };
    if (variant === "ghost") return { ...base, background: "transparent", color: T.textMuted };
    return { ...base, background: "transparent", color: T.text, borderColor: T.border }; // secondary
}

// ─── Formatting ────────────────────────────────────────────────────────────
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const monthShort = (m) => MONTHS[m - 1];
export const period = (y, m) => `${MONTHS[m - 1]} ${y}`;

export function money(n, { sign = false } = {}) {
    if (n === null || n === undefined || n === "" || isNaN(Number(n))) return "—";
    const v = Number(n);
    const s = Math.abs(v).toLocaleString("en-IN", { maximumFractionDigits: 2 });
    return `${v < 0 ? "−" : sign && v > 0 ? "+" : ""}NPR ${s}`;
}

// "2026-10-01" or ISO timestamp → "Oct 1, 2026"
export function fmtDay(v) {
    if (!v) return "—";
    const d = /^\d{4}-\d{2}-\d{2}$/.test(v) ? new Date(`${v}T00:00:00`) : new Date(v.endsWith?.("Z") || v.includes?.("+") ? v : `${v}Z`);
    if (isNaN(d)) return "—";
    return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}
export function fmtDateTime(v) {
    if (!v) return "—";
    const d = new Date(v.endsWith?.("Z") || v.includes?.("+") ? v : `${v}Z`);
    if (isNaN(d)) return "—";
    return `${fmtDay(d.toISOString())} · ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
}

export const METHODS = [
    ["bank_transfer", "Bank transfer"],
    ["esewa", "eSewa"],
    ["khalti", "Khalti"],
    ["cash", "Cash"],
    ["cheque", "Cheque"],
    ["other", "Other"],
];
export const methodLabel = (m) => (METHODS.find(([k]) => k === m) || [null, m ? m.replace(/_/g, " ") : "—"])[1];
